export interface CharacterDef {
  id: string;
  name: string;
  description: string;
  modelUrl: string;
}

export interface StatDef {
  id: string;
  label: string;
  abbr: string;
  tooltip: string;
}

/**
 * The player-selectable bodies. These are the ONLY character models the player
 * picks from; settlement NPCs draw from the separate `npc-*.glb` set (see
 * `ultra_map_editor/vite-plugin.ts`'s `CHARACTER_MODEL_RE`), so adding a
 * preset here does not put it in the world, and adding an NPC body does not
 * offer it to the player.
 *
 * **`id` is a persisted value.** `Character.presetId` stores it and it is
 * locked after creation, so an id here can be added but never renamed or
 * removed without orphaning existing characters. It must also appear in the
 * backend's `DEFAULT_CHARACTER_CONFIG.presetIds` — `CreateCharacterDto`
 * validates against that list, so a preset offered here and missing there is a
 * 400 the frontend's own (API-mocking) tests cannot see. `characters.contract.test.ts`
 * is what actually holds the two sides together.
 *
 * TODO(copy): names and descriptions are placeholder. They predate the current
 * art — the models were replaced wholesale on 2026-09-09 with a new low-poly
 * set, so "Voxel — blocky and battle-hardened" et al. no longer describe the
 * body they are attached to, and 5-8 have never had real copy at all.
 */
export const CHARACTERS: CharacterDef[] = [
  {
    id: 'char-1',
    name: 'Voxel',
    description: 'Blocky and battle-hardened.',
    modelUrl: '/models/char-1.glb',
  },
  {
    id: 'char-2',
    name: 'Drifter',
    description: 'Light on their feet, quick with a blade.',
    modelUrl: '/models/char-2.glb',
  },
  {
    id: 'char-3',
    name: 'Scrapper',
    description: 'Built from the ruins, tough as scrap metal.',
    modelUrl: '/models/char-3.glb',
  },
  {
    id: 'char-4',
    name: 'Spark',
    description: 'Small frame, big plans.',
    modelUrl: '/models/char-4.glb',
  },
  {
    id: 'char-5',
    name: 'Ash',
    description: 'Quiet, watchful, hard to surprise.',
    modelUrl: '/models/char-5.glb',
  },
  {
    id: 'char-6',
    name: 'Rook',
    description: 'Broad shoulders, longer patience.',
    modelUrl: '/models/char-6.glb',
  },
  {
    id: 'char-7',
    name: 'Wren',
    description: 'Travels light and moves first.',
    modelUrl: '/models/char-7.glb',
  },
  {
    id: 'char-8',
    name: 'Flint',
    description: 'Grew up after the lights went out.',
    modelUrl: '/models/char-8.glb',
  },
];

/**
 * ─── The six-stat table that used to live here is GONE ──────────────────────
 * `STATS` (STR/AGI/END/PER/INT/CHA), `STAT_BASE`, `STAT_MAX` and
 * `BONUS_POINTS` described a creation-time stat-buy that the backend has never
 * supported, and they described the wrong attributes besides: the game has
 * FIVE — STR, INT, STA, AGI, PER — with no END and no CHA, and
 * `POST /character` grants them itself and banks no free points. The chooser's
 * buy panel spent six points into this table and dropped them on Continue.
 *
 * The live attribute set is the backend's `ATTRIBUTE_CODE_TO_FIELD`
 * (`game/character/character.types.ts`), reaching the frontend as
 * `CharacterSheet.attributes`; points are spent after creation through the
 * Survivor Record's skill-points stage. Deleted rather than left unused so
 * nothing wires a contradicting second source of truth back in.
 *
 * `StatDef` above is kept — nothing else uses it today, but it is the shape a
 * real attribute table would take.
 */

